import { z } from 'zod';
import { queryOptions } from '@tanstack/react-query';
import { DATA_BASE_URL } from './loaders';

export const COUNTRY_CODES = [
  // Core 30 — original roster
  'AE', 'PT', 'ES', 'CY', 'TH', 'MY', 'ID', 'GR', 'MU', 'IT',
  'CR', 'MT', 'PA', 'EE', 'ME', 'RS', 'GE', 'MX', 'SG', 'UY',
  'AL', 'HU', 'BR', 'PY', 'VN', 'AR', 'TR', 'HR', 'ZA', 'CO',
  // Europe expansion — fully scored
  'AT',
  // Career-track expansion (CV.2 M4+) — stubs only, no metrics yet
  'CA', 'CL', 'CZ', 'DE', 'FR', 'GB', 'IE', 'NL', 'PL',
] as const;

export type CountryCode = (typeof COUNTRY_CODES)[number];

export const DEFAULT_COUNTRY: CountryCode = 'IT';

const COUNTRY_CODE_SET = new Set<string>(COUNTRY_CODES);

export function isCountryCode(v: unknown): v is CountryCode {
  return typeof v === 'string' && COUNTRY_CODE_SET.has(v);
}

export const CountryTierSchema = z.enum(['T0', 'T1', 'T2', 'T3']);
export type CountryTier = z.infer<typeof CountryTierSchema>;

// Metrics used by the quiz scoring engine. Optional per country —
// when missing, country is excluded from scoring and shown as "coming soon".
export const CountryMetricsSchema = z.object({
  climate: z.object({
    jul_max_c: z.number(),
    jan_min_c: z.number(),
    // Optional regional range [coolest_region, hottest_region] for countries
    // with significant geographic diversity (e.g. Italy Alps→Sicily).
    jul_max_range: z.tuple([z.number(), z.number()]).optional(),
    jan_min_range: z.tuple([z.number(), z.number()]).optional(),
    humidity_pct: z.number(),
    sunshine_h: z.number(),
    // Climate type tag — matches options of the climate question.
    type: z.enum(['alps', 'mediterranean', 'continental', 'tropical', 'northern']).array(),
  }),
  cost: z.object({
    // Monthly EUR for a small family (rent + groceries + utilities).
    monthly_eur_family: z.number(),
    rent_capital_eur: z.number(),
  }),
  tax: z.object({
    pit_top_rate_pct: z.number(),
    // Special flat regime for freelancers/self-employed, percent (null if none).
    flat_freelance_pct: z.number().nullable(),
    worldwide_taxation: z.boolean(),
  }),
  visa: z.object({
    schengen: z.boolean(),
    freelance_path: z.boolean(),
    digital_nomad: z.boolean(),
    years_to_citizenship: z.number().nullable(),
    // Open to RU passport applicants in practice (post-sanctions reality).
    open_to_ru: z.boolean(),
  }),
  language: z.object({
    // 'very_high' | 'high' | 'moderate' | 'low' — EF EPI bands
    english_band: z.enum(['very_high', 'high', 'moderate', 'low']),
    official: z.array(z.string()),
  }),
  safety: z.object({
    gpi: z.number(), // Global Peace Index 1.0..4.0 (lower = better)
  }),
  healthcare: z.object({
    uhc_index: z.number(), // 0..100
  }),
  jobs: z.object({
    internet_mbps: z.number(),
  }),
  family: z.object({
    school_quality: z.number(), // 0..1
    english_schools: z.boolean(),
  }),
  banking: z.object({
    // 'yes' | 'limited' | 'no' — pratique для RU паспорта на момент данных
    ru_passport: z.enum(['yes', 'limited', 'no']),
    swift_for_ru: z.boolean(),
  }),
});

export type CountryMetrics = z.infer<typeof CountryMetricsSchema>;

export const CountrySchema = z.object({
  code: z.enum(COUNTRY_CODES),
  iso3: z.string().length(3),
  name_en: z.string(),
  name_ru: z.string(),
  flag_emoji: z.string(),
  region: z.string(),
  tier: CountryTierSchema,
  rating: z.number().min(0).max(10),
  visa_programs: z.array(z.string()),
  data_available: z.boolean(),
  map_url: z.string().optional(),
  metrics: CountryMetricsSchema.optional(),
});

export type Country = z.infer<typeof CountrySchema>;

export const CountriesFileSchema = z.object({
  version: z.string(),
  generated: z.string(),
  countries: z.array(CountrySchema),
});

export type CountriesFile = z.infer<typeof CountriesFileSchema>;

async function loadCountries(): Promise<CountriesFile> {
  const res = await fetch(`${DATA_BASE_URL}/countries.json`);
  if (!res.ok) throw new Error(`Failed to load countries.json — ${res.status}`);
  return CountriesFileSchema.parse(await res.json());
}

export const countriesQuery = () =>
  queryOptions({
    queryKey: ['countries'] as const,
    queryFn: loadCountries,
    staleTime: Infinity,
    gcTime: Infinity,
  });
